import { useMemo } from 'react';
import { usePreferences } from '../../hooks/usePreferences';

// Re-export pure helpers so existing call sites can import everything
// from one place; the underlying functions live in `sub-id.ts` so
// they're testable without dragging the React Native module graph in.
export { appendSubId, buildSubId } from './sub-id';

export interface AffiliateDefaults {
  /** Adults count. Computed from `participantCount` (typically
   *  `Trip.participants.length`), clamped to >= 1. Undefined when the
   *  trip has no participants list; callers omit the field and the
   *  partner page defaults to 1. */
  adults?: number | undefined;
  /** ISO 4217 — user's preferred currency from `usePreferences()`. */
  currency: string;
  /** BCP-47 locale from `Intl.DateTimeFormat().resolvedOptions().locale`.
   *  Cross-platform — works on web (`navigator.language`) and React
   *  Native (Hermes / JSC both ship Intl in modern Expo). Falls back
   *  to 'en-US' if Intl is absent for any reason. */
  locale: string;
}

/**
 * Contextual defaults forwarded to every affiliate URL builder so the
 * partner search page lands on the user's currency and language and
 * the right adult count. Lifts conversion measurably vs. raw destination
 * searches — partner pages that don't have to re-prompt the visitor
 * for currency/locale convert at ~1.2–1.5× the rate, per the TP
 * partner docs.
 *
 * Use as a hook inside components — `usePreferences` is itself a
 * React Query call, so this must be called inside a render path. Pass
 * `participantCount` from `Trip.participants?.length` at the call site
 * — kept as a primitive so this helper doesn't pull in the full Trip
 * type.
 */
export function useAffiliateDefaults(
  participantCount?: number | undefined,
): AffiliateDefaults {
  const { preferredCurrency } = usePreferences();
  return useMemo(() => {
    const count = participantCount ?? 0;
    return {
      adults: count > 0 ? Math.max(1, count) : undefined,
      currency: preferredCurrency,
      locale: resolveLocale(),
    };
  }, [participantCount, preferredCurrency]);
}

function resolveLocale(): string {
  try {
    const tag = new Intl.DateTimeFormat().resolvedOptions().locale;
    return tag || 'en-US';
  } catch {
    return 'en-US';
  }
}
